import { environment } from 'src/environments/environment';
import { Api } from './api';
import { z } from 'zod';
import { Currency } from 'src/app/model/wallet/currency';
import { HttpService } from '../services/http/http.service';

export const SET_LIGHTNING_ADDRESS = 'accounts/set-lightning-address';
export const CREATE_INVOICE = 'jit/create-invoice';
export const DEPOSIT_STATUS = 'jit/deposit-status';

export class JitApi {
  private api: Api;

  private constructor(httpService: HttpService) {
    // JIT also uses the wallets cloud project
    this.api = new Api(environment.walletApi, httpService);
  }

  static request(httpService: HttpService) {
    return new JitApi(httpService);
  }

  async setLightningAddress(lightningAddress: string): Promise<ApiResponseLightningAddress> {
    const response = await this.api.post<ApiResponseLightningAddress, { lightningAddress: string }>(
      SET_LIGHTNING_ADDRESS,
      { lightningAddress },
    );
    if (!JitApi.isApiResponseLightningAddress(response)) {
      console.warn(`${SET_LIGHTNING_ADDRESS} api response has changed`);
    }
    return response;
  }

  async createInvoice(usd: number): Promise<ApiResponseCreateInvoice> {
    const response = await this.api.post<
      ApiResponseCreateInvoice,
      { amount: number; currency: string }
    >(CREATE_INVOICE, { amount: usd, currency: Currency.USD });
    if (!JitApi.isApiResponseCreateInvoice(response)) {
      console.warn(`${CREATE_INVOICE} api response has changed`);
    }
    return response;
  }

  async depositStatus(depositId: string): Promise<ApiResponseDepositStatus> {
    const response = await this.api.get<ApiResponseDepositStatus>(`${DEPOSIT_STATUS}/${depositId}`);
    if (!JitApi.isApiResponseDepositStatus(response)) {
      console.warn(`${DEPOSIT_STATUS} api response has changed`);
    }

    return response;
  }

  static isApiResponseLightningAddress(obj: unknown): obj is ApiResponseLightningAddress {
    const dataSchema = z.object({
      lightningAddress: z.string(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseCreateInvoice(obj: unknown): obj is ApiResponseCreateInvoice {
    const dataSchema = z.object({
      amountSats: z.number(),
      expiresAt: z.number(),
      lnInvoice: z.string(),
      depositId: z.string(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseDepositStatus(obj: unknown): obj is ApiResponseDepositStatus {
    const dataSchema = z.object({
      chargeStatus: z.string(),
      depositStatus: z.string(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }
}

export interface ApiResponseLightningAddress {
  msg: string;
  status: number;
  data: {
    lightningAddress: string;
  };
}

export interface ApiResponseCreateInvoice {
  status: number;
  msg: string;
  data: {
    amountSats: number;
    expiresAt: number;
    lnInvoice: string;
    depositId: string;
  };
}

export interface ApiResponseDepositStatus {
  status: number;
  msg: string;
  data: {
    chargeStatus: string;
    depositStatus: string;
  };
}
